import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

const texturePaths = {
  lightGreyFabricTexture: "/assets/textures/white-sm.jpg",
  blueLeatherTexture: "/assets/textures/blue-sm.jpg",
  lightBlueFabricTexture: "/assets/textures/sky-sm.jpg",
  darkGreyFabricTexture: "/assets/textures/grey-sm.jpg",
  baseColor: true,
};

const getModel = () => {
  const scene = document.querySelector("a-scene");
  if (!scene) return null;
  const chairModel = scene.querySelector("#chairModel");
  if (chairModel) {
    return chairModel;
  }
};

const getBaseModelTexture = () => {
  const chairModel = getModel();
  if (!chairModel?.object3D) return null;
  let texture = null;
  chairModel.object3D.traverse((node) => {
    if (node.isMesh) {
      texture = node.material.map.clone();
    }
  });
  return texture;
};

var baseModelTexture = null;

const handleModelLoaded = () => {
  setTimeout(() => {
    baseModelTexture = getBaseModelTexture();
    // console.log('loaded.baseModelTexture', baseModelTexture);
  }, 1000);
};

function preloadTextures(onLoaded) {
  const loader = new THREE.TextureLoader();
  const textures = {};
  let loadedCount = 0;
  const keys = Object.keys(texturePaths);
  keys.forEach((key) => {
    if (key !== "baseColor") {
      loader.load(texturePaths[key], (texture) => {
        textures[key] = texture;
        loadedCount++;
        if (loadedCount === keys.length) {
          onLoaded(textures);
        }
      });
    } else {
      textures["baseColor"] = baseModelTexture;
      loadedCount++;
      if (loadedCount === keys.length) {
        onLoaded(textures);
      }
    }
  });
}

const ARChairViewer = ({
  onChairLoaded,
  onMarkerVisibleChange,
  onMaterialChanged,
}) => {
  const chairModelRef = useRef();
  const markerEntityRef = useRef();
  const texturesRef = useRef({});
  const [showTitle, setShowTitle] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [markerVisible, setMarkerVisible] = useState(false);

  useEffect(() => {
    const modalEl = document.getElementById("avatarModel");

    modalEl.addEventListener("loaded", handleModelLoaded);
    return () => {
      modalEl.removeEventListener("loaded", handleModelLoaded);
    };
  }, []);

  useEffect(() => {
    // Preload textures
    // console.log('on track change', texturesRef.current);
    preloadTextures((loadedTextures) => {
      texturesRef.current = { ...loadedTextures };
    });

    // Add meta tags
    const metaTags = [
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        "http-equiv": "Cache-Control",
        content: "no-cache, no-store, must-revalidate",
      },
      { "http-equiv": "Pragma", content: "no-cache" },
      { "http-equiv": "Expires", content: "0" },
    ];

    metaTags.forEach((tag) => {
      const meta = document.createElement("meta");
      Object.entries(tag).forEach(([key, value]) => {
        meta.setAttribute(key, value);
      });
      document.head.appendChild(meta);
    });

    // Load scripts tuần tự
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = false; // Đảm bảo tuần tự
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const loadScripts = async () => {
      try {
        await loadScript("https://aframe.io/releases/1.6.0/aframe.min.js");
        await loadScript(
          "https://cdn.jsdelivr.net/npm/mind-ar@1.2.5/dist/mindar-image-aframe.prod.js"
        );
        await loadScript("/js/material-switcher.js");
      } catch (error) {
        console.error("Error loading scripts:", error);
      }
    };

    loadScripts();

    // Lắng nghe sự kiện đổi vật liệu
    const handleChangeMaterial = (e) => {
      const materialId = e.detail;
      const scene = document.querySelector("a-scene");
      if (!scene) return;
      const chairModel = scene.querySelector("#chairModel");
      const texture = texturesRef.current[materialId];
      if (chairModel && (texture || materialId === "baseColor")) {
        chairModel.object3D.traverse((node) => {
          if (node.isMesh) {
            node.material.transparent = true;
            let opacity = 1;
            const fadeOut = () => {
              opacity -= 0.1;
              if (opacity <= 0) {
                opacity = 0;
                node.material.opacity = opacity;
                if (materialId === "baseColor") {
                  // console.log('materialId.baseColor', baseModelTexture);
                  node.material.map = baseModelTexture;
                } else {
                  node.material.map = texture;
                }
                node.material.needsUpdate = true;
                // Fade in
                let opacityIn = 0;
                const fadeIn = () => {
                  opacityIn += 0.1;
                  if (opacityIn >= 1) {
                    opacityIn = 1;
                    node.material.opacity = opacityIn;
                    if (typeof onMaterialChanged === "function") {
                      onMaterialChanged(materialId);
                    }
                  } else {
                    node.material.opacity = opacityIn;
                    requestAnimationFrame(fadeIn);
                  }
                };
                fadeIn();
              } else {
                node.material.opacity = opacity;
                requestAnimationFrame(fadeOut);
              }
            };
            fadeOut();
          }
        });
      }
    };
    window.addEventListener("change-material", handleChangeMaterial);

    // Lắng nghe model ghế load xong
    const chairModelEl = chairModelRef.current;
    const handleModelLoaded = () => {
      setModelLoaded(true);
      if (onChairLoaded) onChairLoaded();
    };
    if (chairModelEl) {
      chairModelEl.addEventListener("model-loaded", handleModelLoaded);
    }

    // Lắng nghe marker xuất hiện/biến mất
    const markerEntityEl = markerEntityRef.current;
    const handleMarkerFound = () => {
      setMarkerVisible(true);
      if (onMarkerVisibleChange) onMarkerVisibleChange(true);
    };
    const handleMarkerLost = () => {
      setMarkerVisible(false);
      if (onMarkerVisibleChange) onMarkerVisibleChange(false);
    };
    if (markerEntityEl) {
      markerEntityEl.addEventListener("targetFound", handleMarkerFound);
      markerEntityEl.addEventListener("targetLost", handleMarkerLost);
    }

    // Cleanup
    return () => {
      metaTags.forEach((tag) => {
        const meta = document.querySelector(
          `meta[${Object.keys(tag)[0]}="${Object.values(tag)[0]}"]`
        );
        if (meta) {
          meta.remove();
        }
      });
      window.removeEventListener("change-material", handleChangeMaterial);
      if (chairModelEl) {
        chairModelEl.removeEventListener("model-loaded", handleModelLoaded);
      }
      if (markerEntityEl) {
        markerEntityEl.removeEventListener("targetFound", handleMarkerFound);
        markerEntityEl.removeEventListener("targetLost", handleMarkerLost);
      }
    };
  }, [onChairLoaded, onMarkerVisibleChange, onMaterialChanged]);

  useEffect(() => {
    setShowTitle(modelLoaded && markerVisible);
  }, [modelLoaded, markerVisible]);

  return (
    <div className="ar-fullscreen" style={{ position: "relative" }}>
      {showTitle && (
        <div
          style={{
            position: "absolute",
            top: 32,
            left: "50%",
            transform: "translateX(-50%)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            textAlign: "left",
            zIndex: 10001,
            width: 380,
            maxWidth: "90vw",
            lineHeight: 1.13,
            userSelect: "none",
            background: "none",
            padding: 0,
          }}
        >
          <div>MODERN ARM CHAIR</div>
          <div
            style={{
              marginTop: "14px",
              width: "55%",
              height: 4,
              background: "#fff",
            }}
          />
        </div>
      )}
      <a-scene
        mindar-image="imageTargetSrc: ./assets/qrcode.mind;"
        color-space="sRGB"
        renderer="colorManagement: true, physicallyCorrectLights"
        vr-mode-ui="enabled: false"
        device-orientation-permission-ui="enabled: false"
      >
        <a-assets>
          <a-asset-item
            id="avatarModel"
            src="./assets/chair.glb"
          ></a-asset-item>
        </a-assets>
        <a-camera position="0 0 0" look-controls="enabled: false"></a-camera>
        <a-entity mindar-image-target="targetIndex: 0" ref={markerEntityRef}>
          <a-gltf-model
            id="chairModel"
            ref={chairModelRef}
            rotation="0 0 0 "
            position="0 -1 0"
            scale="0.03 0.03 0.03"
            src="#avatarModel"
            animation="property: position; to: 0 -0.7 0.1; dur: 1000; easing: easeInOutQuad; loop: true; dir: alternate"
            animation__rotate="property: rotation; to: 0 359.99 0; dur: 12000; easing: linear; loop: true;"
          ></a-gltf-model>
          <a-light type="point" position="0 0 10" intensity="1"></a-light>
        </a-entity>
      </a-scene>
    </div>
  );
};

export default ARChairViewer;
